import { Guid } from "client_legacy/ndaw/guid";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import {
	MailboxIdentifier,
	MailboxIdentifierKind,
	FolderIdentifier,
	FolderIdentifierKind,
	getLiveInformation,
	LiveInformationRequestModel,
	LiveInformationResponseModel,
	Interests,
	convertMailboxIdentifierToModel,
	convertFolderIdentifierToModel,
	unknownMailboxIdentifier,
	unknownFolderIdentifier,
	UnreadCountResult,
	ItemsResult
} from "client/Api/Workspace/LiveInformation/Email/emailLiveInformation";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";

interface EmailLiveInformationProviderOptions {
	intervalInSeconds: number;
}

export type EmailsResourceId = {
	mailbox: MailboxIdentifier;
	folder: FolderIdentifier;
};

export interface InterestedEmailParty extends InterestedParty<Guid, EmailsResourceId> {
	informAboutExchangeNotBeingConfigured?: () => void;
	updateUnreadCount?(result: UnreadCountResult | nil): void;
	updateItems?(result: ItemsResult | nil): void;
}

export class EmailLiveInformationProvider extends LiveInformationProvider<
	EmailLiveInformationProviderOptions,
	Guid,
	EmailsResourceId,
	Interests,
	InterestedEmailParty,
	LiveInformationResponseModel
	>
{
	private readonly _isExchangeConfigured: boolean;
	private readonly _isExchangeEnabled: boolean;

	public get isExchangeEnabled(): boolean {
		return this._isExchangeEnabled;
	}
	public get isExchangeAvailable(): boolean {
		return this._isExchangeConfigured && this._isExchangeEnabled;
	}

	constructor(options: EmailLiveInformationProviderOptions) {
		super(options);

		const { exchange } = userConfiguration();
		this._isExchangeConfigured = exchange.isConfigured;
		this._isExchangeEnabled = exchange.isEnabled;
	}

	protected async getDataFromBackend(entries: ReadonlyArray<[EmailsResourceId, Interests]>)
		: Promise<ReadonlyArray<[EmailsResourceId, LiveInformationResponseModel]>>
	{
		if (!this.isExchangeAvailable) {
			return Promise.reject();
		}

		const models: LiveInformationRequestModel[] = entries.map(
			([resourceId, interests]) => {
				const model: LiveInformationRequestModel = {
					mailbox: convertMailboxIdentifierToModel(resourceId.mailbox),
					folder: convertFolderIdentifierToModel(resourceId.folder),
					interestedIn: interests
				};

				return model;
			}
		);

		const responses: LiveInformationResponseModel[] = await getLiveInformation(models);

		return responses.map(
			(x) => [{ mailbox: x.mailbox, folder: x.folder }, x]
		);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<Interests, LiveInformationResponseModel>,
		response: [Interests, LiveInformationResponseModel]
	) {
		const [retrievedInterests, model] = response;
		if (retrievedInterests.items) {
			cacheItem.retrievedInterests.items = true;
			cacheItem.data.data.items = model.data.items;
		}
		if (retrievedInterests.unreadItems) {
			cacheItem.retrievedInterests.unreadItems = true;
			cacheItem.data.data.unreadItems = model.data.unreadItems;
		}

		if (retrievedInterests.unreadCount) {
			cacheItem.retrievedInterests.unreadCount = true;
			cacheItem.data.data.unreadCount = model.data.unreadCount;
		}
	}

	protected override informAboutGlobalRetrievalFailure(party: InterestedEmailParty) {
		if (!this.isExchangeAvailable) {
			if (party.informAboutExchangeNotBeingConfigured) {
				party.informAboutExchangeNotBeingConfigured();
			}
			return;
		}

		super.informAboutGlobalRetrievalFailure(party);
	}

	protected informParty(
		party: InterestedEmailParty,
		cacheItem: [Interests, LiveInformationResponseModel]
	) {
		if (!this.isExchangeAvailable) {
			if (party.informAboutExchangeNotBeingConfigured) {
				party.informAboutExchangeNotBeingConfigured();
			}
			return;
		}

		const [retrievedInterests, response] = cacheItem;
		const interestedIn: Interests = this.getInterestsForEnsuredParty(party);

		if (interestedIn.unreadCount && retrievedInterests.unreadCount) {
			if (party.updateUnreadCount) {
				party.updateUnreadCount(response.data.unreadCount);
			}
		}

		if (interestedIn.unreadItems && retrievedInterests.unreadItems) {
			if (party.updateItems) {
				party.updateItems(response.data.unreadItems);
			}
		}

		if (interestedIn.items && retrievedInterests.items) {
			if (party.updateItems) {
				party.updateItems(response.data.items);
			}
		}
	}

	protected override createResourceMap<TValue>(): Map<EmailsResourceId, TValue> {
		return new NormalizedMap<EmailsResourceId, TValue>(normalizeKey);
	}
}

function normalizeKey(key: EmailsResourceId): string {
	const { mailbox, folder } = key;
	return normalizeMailboxIdentifier(mailbox) + "_" + normalizeFolderIdentifier(folder);
}

function normalizeMailboxIdentifier(mailboxId: MailboxIdentifier): string {
	switch (mailboxId.kind) {
		case MailboxIdentifierKind.Default:
			return `${mailboxId.kind}`;
		case MailboxIdentifierKind.Specific:
			return `${mailboxId.kind}_${(mailboxId.smtpAddress || "").toLowerCase()}`;
		default:
			return unknownMailboxIdentifier(mailboxId);
	}
}

function normalizeFolderIdentifier(folderId: FolderIdentifier): string {
	switch (folderId.kind) {
		case FolderIdentifierKind.WellKnown:
			return `${folderId.kind}_${folderId.folder}`;
		case FolderIdentifierKind.Specific:
			return `${folderId.kind}_${folderId.id}`;
		default:
			return unknownFolderIdentifier(folderId);
	}
}
